import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { FaPlus, FaPencilAlt, FaBan, FaCheck, FaArrowLeft, FaGlobeAmericas, FaExclamationTriangle } from "react-icons/fa";
import Modal from "../Modal";

const API = "/api/misioneros";

const TITULOS = ["Misionero", "Evangelista", "Apoyo en Traducción de la Biblia"];

const formatoFecha = (fecha) =>
  fecha ? new Date(fecha).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" }) : "";

const formatoMoneda = (numero) =>
  Number(numero || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function MisionerosAdmin() {
  const [misioneros, setMisioneros] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ mostrar: false, mensaje: "", tipo: "success" });

  const [modalOpen, setModalOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState({
    nombre: "", es_de_casa: true, lugar_llamado: "", pais: "",
    titulo: TITULOS[0], cantidad_apoyo_mensual: "", fecha_inicio: "",
  });
  const [imagenFile, setImagenFile] = useState(null);
  const [preview, setPreview] = useState(null);

  const [bajaOpen, setBajaOpen] = useState(false);
  const [misioneroBaja, setMisioneroBaja] = useState(null);
  const [fechaFinBaja, setFechaFinBaja] = useState("");

  const mostrarToast = (mensaje, tipo = "success") => {
    setToast({ mostrar: true, mensaje, tipo });
    setTimeout(() => setToast({ mostrar: false, mensaje: "", tipo: "success" }), 3000);
  };

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(API, { credentials: "include" });
      if (!res.ok) throw new Error("Error obteniendo misioneros");
      setMisioneros(await res.json());
    } catch (err) {
      console.error(err);
      mostrarToast("Error cargando misioneros", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const abrirModal = () => {
    setEditando(null);
    setForm({ nombre: "", es_de_casa: true, lugar_llamado: "", pais: "", titulo: TITULOS[0], cantidad_apoyo_mensual: "", fecha_inicio: "" });
    setImagenFile(null);
    setPreview(null);
    setModalOpen(true);
  };

  const abrirModalEditar = (m) => {
    setEditando(m);
    setForm({
      nombre: m.nombre,
      es_de_casa: m.es_de_casa,
      lugar_llamado: m.lugar_llamado || "",
      pais: m.pais || "",
      titulo: m.titulo || TITULOS[0],
      cantidad_apoyo_mensual: m.cantidad_apoyo_mensual || "",
      fecha_inicio: m.fecha_inicio?.slice(0, 10) || "",
    });
    setImagenFile(null);
    setPreview(m.imagen ? `${m.imagen}` : null);
    setModalOpen(true);
  };

  const cerrarModal = () => setModalOpen(false);

  const onSeleccionarImagen = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImagenFile(file);
    setPreview(URL.createObjectURL(file));
  };

  const guardar = async () => {
    if (!form.nombre.trim() || !form.fecha_inicio) {
      mostrarToast("Nombre y fecha de inicio son obligatorios", "error");
      return;
    }

    const data = new FormData();
    data.append("nombre", form.nombre);
    data.append("es_de_casa", form.es_de_casa);
    data.append("lugar_llamado", form.lugar_llamado);
    data.append("pais", form.pais);
    data.append("titulo", form.titulo);
    data.append("cantidad_apoyo_mensual", form.cantidad_apoyo_mensual);
    data.append("fecha_inicio", form.fecha_inicio);
    if (imagenFile) data.append("imagen", imagenFile);

    try {
      const res = await fetch(editando ? `${API}/${editando.id_misionero}` : API, {
        method: editando ? "PUT" : "POST",
        credentials: "include",
        body: data,
      });

      if (!res.ok) {
        const err = await res.json();
        mostrarToast(err.mensaje || "Error guardando misionero", "error");
        return;
      }

      mostrarToast(editando ? "Misionero actualizado" : "Misionero creado");
      setModalOpen(false);
      cargar();
    } catch (err) {
      console.error(err);
      mostrarToast("Error inesperado", "error");
    }
  };

  const abrirBaja = (m) => {
    setMisioneroBaja(m);
    setFechaFinBaja(new Date().toISOString().slice(0, 10));
    setBajaOpen(true);
  };

  const confirmarBaja = async () => {
    if (!fechaFinBaja) {
      mostrarToast("La fecha de término es obligatoria", "error");
      return;
    }
    try {
      const res = await fetch(`${API}/${misioneroBaja.id_misionero}`, {
        method: "DELETE",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fecha_fin: fechaFinBaja }),
      });
      if (!res.ok) {
        const err = await res.json();
        mostrarToast(err.mensaje || "Error dando de baja", "error");
        return;
      }
      mostrarToast("Misionero dado de baja");
      setBajaOpen(false);
      cargar();
    } catch (err) {
      console.error(err);
      mostrarToast("Error inesperado", "error");
    }
  };

  const reactivar = async (m) => {
    try {
      const res = await fetch(`${API}/${m.id_misionero}/reactivar`, {
        method: "PATCH",
        credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json();
        mostrarToast(err.mensaje || "Error reactivando misionero", "error");
        return;
      }
      mostrarToast("Misionero reactivado");
      cargar();
    } catch (err) {
      console.error(err);
      mostrarToast("Error inesperado", "error");
    }
  };

  return (
    <div className="p-6 bg-slate-50">

      <div className="flex items-center justify-between mb-6">
        <div>
          <Link to="/dashboard/administracion" className="text-sm text-slate-500 hover:text-indigo-600 flex items-center gap-1 mb-1">
            <FaArrowLeft className="text-xs" /> Administración
          </Link>
          <h1 className="text-2xl font-bold text-slate-800">Misioneros</h1>
          <p className="text-sm text-slate-500">
            Los misioneros activos se muestran automáticamente en el dashboard.
          </p>
        </div>

        <button
          onClick={abrirModal}
          className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition"
        >
          <FaPlus /> Nuevo misionero
        </button>
      </div>

      {toast.mostrar && (
        <div className={`fixed top-5 right-5 z-[70] px-4 py-3 rounded-lg shadow-lg text-sm text-white ${toast.tipo === "success" ? "bg-emerald-600" : "bg-rose-600"}`}>
          {toast.mensaje}
        </div>
      )}

      {/* Modal Nuevo/Editar */}
      <Modal
        open={modalOpen}
        onClose={cerrarModal}
        title={editando ? "Editar misionero" : "Nuevo misionero"}
        maxWidth="max-w-3xl"
      >
        <div>
          <div className="grid md:grid-cols-2 gap-6">

            {/* IMAGEN */}
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Fotografía</label>
              <div className="w-full aspect-[4/5] bg-slate-100 rounded-lg border border-slate-200 overflow-hidden flex items-center justify-center">
                {preview ? (
                  <img src={preview} alt="preview" className="w-full h-full object-cover" />
                ) : (
                  <FaGlobeAmericas className="text-4xl text-slate-300" />
                )}
              </div>
              <input
                type="file"
                accept="image/png, image/jpeg, image/webp"
                onChange={onSeleccionarImagen}
                className="mt-2 text-sm text-slate-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-indigo-50 file:text-indigo-700 file:text-xs file:font-medium hover:file:bg-indigo-100"
              />
              <p className="text-xs text-slate-400 mt-1.5">
                Tamaño recomendado: 800 x 1000 px (relación 4:5), máximo 5 MB. Formatos JPG, PNG o WEBP.
                Usa una foto donde la persona se vea centrada para que se muestre bien en el carrusel del dashboard.
              </p>
            </div>

            {/* CAMPOS */}
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Nombre <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="Nombre completo"
                  className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
                  value={form.nombre}
                  onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Título</label>
                  <select
                    className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
                    value={form.titulo}
                    onChange={(e) => setForm({ ...form, titulo: e.target.value })}
                  >
                    {TITULOS.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">¿Es de casa?</label>
                  <select
                    className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
                    value={form.es_de_casa ? "si" : "no"}
                    onChange={(e) => setForm({ ...form, es_de_casa: e.target.value === "si" })}
                  >
                    <option value="si">Sí</option>
                    <option value="no">No</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Lugar donde fue llamado</label>
                  <input
                    type="text"
                    placeholder="Lugar donde fue llamado"
                    className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
                    value={form.lugar_llamado}
                    onChange={(e) => setForm({ ...form, lugar_llamado: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">País</label>
                  <input
                    type="text"
                    placeholder="País"
                    className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
                    value={form.pais}
                    onChange={(e) => setForm({ ...form, pais: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Apoyo mensual</label>
                  <input
                    type="number"
                    placeholder="0.00"
                    className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
                    value={form.cantidad_apoyo_mensual}
                    onChange={(e) => setForm({ ...form, cantidad_apoyo_mensual: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    Fecha de inicio <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="date"
                    className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
                    value={form.fecha_inicio}
                    onChange={(e) => setForm({ ...form, fecha_inicio: e.target.value })}
                  />
                </div>
              </div>
              <p className="text-xs text-slate-400">
                La fecha de término solo se registra al dar de baja al misionero.
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-slate-100">
            <button onClick={cerrarModal} className="px-3.5 py-1.5 text-sm rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition">
              Cancelar
            </button>
            <button onClick={guardar} className="px-3.5 py-1.5 text-sm rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-medium transition">
              {editando ? "Actualizar" : "Guardar"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal dar de baja */}
      <Modal open={bajaOpen} onClose={() => setBajaOpen(false)} maxWidth="max-w-sm">
        <div className="text-center">
          <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 bg-rose-50 text-rose-500">
            <FaExclamationTriangle />
          </div>
          <h3 className="text-lg font-semibold text-slate-800 mb-1">Dar de baja a {misioneroBaja?.nombre}</h3>
          <p className="text-sm text-slate-500 mb-4">Indica la fecha de término de su servicio.</p>
          <input
            type="date"
            className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
            value={fechaFinBaja}
            onChange={(e) => setFechaFinBaja(e.target.value)}
          />
          <div className="flex gap-2">
            <button
              onClick={() => setBajaOpen(false)}
              className="w-full px-4 py-2 text-sm rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition"
            >
              Cancelar
            </button>
            <button
              onClick={confirmarBaja}
              className="w-full px-4 py-2 text-sm rounded-lg text-white font-medium transition bg-rose-600 hover:bg-rose-700"
            >
              Confirmar baja
            </button>
          </div>
        </div>
      </Modal>

      {/* Lista */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <p className="text-sm text-slate-400 col-span-full text-center py-10">Cargando misioneros...</p>
        ) : misioneros.length === 0 ? (
          <p className="text-sm text-slate-400 col-span-full text-center py-10">Aún no hay misioneros registrados.</p>
        ) : (
          misioneros.map((m) => (
            <div key={m.id_misionero} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex flex-col">
              <div className="h-40 bg-slate-100 flex items-center justify-center overflow-hidden">
                {m.imagen ? (
                  <img src={`${m.imagen}`} alt={m.nombre} className="w-full h-full object-cover" />
                ) : (
                  <FaGlobeAmericas className="text-3xl text-slate-300" />
                )}
              </div>
              <div className="p-4 flex-1 flex flex-col">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <p className="font-semibold text-sm text-slate-800">{m.nombre}</p>
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${
                    m.activo ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"
                  }`}>
                    {m.activo ? "Activo" : "Inactivo"}
                  </span>
                </div>
                <p className="text-xs text-slate-500">{m.titulo || "—"}</p>
                <p className="text-xs text-slate-500 mb-1">
                  {m.lugar_llamado ? `${m.lugar_llamado} · ` : ""}{m.pais || ""}
                </p>
                <p className="text-xs text-slate-400 mb-3">
                  {m.es_de_casa ? "De casa" : "No de casa"} · ${formatoMoneda(m.cantidad_apoyo_mensual)}/mes
                </p>
                <p className="text-[11px] text-slate-400 mb-3">
                  {formatoFecha(m.fecha_inicio)}{m.fecha_fin ? ` — ${formatoFecha(m.fecha_fin)}` : ""}
                </p>

                <div className="mt-auto flex gap-2">
                  <button
                    onClick={() => abrirModalEditar(m)}
                    className="flex-1 flex items-center justify-center gap-1 text-xs px-3 py-1.5 rounded-md border border-slate-200 text-slate-600 hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-200 transition"
                  >
                    <FaPencilAlt className="text-[10px]" /> Editar
                  </button>
                  {m.activo ? (
                    <button
                      onClick={() => abrirBaja(m)}
                      className="flex-1 flex items-center justify-center gap-1 text-xs px-3 py-1.5 rounded-md border border-rose-200 text-rose-600 hover:bg-rose-50 transition"
                    >
                      <FaBan className="text-[10px]" /> Dar de baja
                    </button>
                  ) : (
                    <button
                      onClick={() => reactivar(m)}
                      className="flex-1 flex items-center justify-center gap-1 text-xs px-3 py-1.5 rounded-md border border-emerald-200 text-emerald-600 hover:bg-emerald-50 transition"
                    >
                      <FaCheck className="text-[10px]" /> Reactivar
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
